import React, { useState } from "react";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { Col, type ColProps, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ReactAsyncSelect } from "@services/ui-components";
import { CUSTOM_CITY_OPTION_VALUE } from "../../../constants/city";
import { CitySelectFieldsBaseProps, Option } from "../../../interfaces/interfaces";
import { getCitySelectValue } from "@me-team/common/src/components/CitySelect/helpers/getCitySelectValue";

interface BranchCityFormValues {
    country: string | number | null;
    city: number | null;
    customCity?: string | null;
}

interface BranchCitySelectFieldsProps extends CitySelectFieldsBaseProps {
    labelColumnProps?: ColProps;
    inputColumnProps?: ColProps;
}

const defaultLabelColumnProps: ColProps = {
    sm: 3,
    xxl: 2,
};

const defaultInputColumnProps: ColProps = {
    sm: 9,
    xxl: 10,
};

const isCustomCityOption = (option: Option | null) => {
    return option ? Number(option.value) === CUSTOM_CITY_OPTION_VALUE : false;
};

const BranchCitySelectFields: React.FC<BranchCitySelectFieldsProps> = ({
    cityFieldId,
    customCityFieldId,
    cityOptions,
    selectedCityOption,
    showCustomCityInput,
    setShowCustomCityInput,
    isLoading,
    onMenuOpen,
    onMenuClose,
    onInputChange,
    onMenuScrollToBottom,
    labelColumnProps = defaultLabelColumnProps,
    inputColumnProps = defaultInputColumnProps,
}) => {
    const { t } = useTranslation();
    const { values, errors, touched, setFieldValue } = useFormikContext<BranchCityFormValues>();
    const [currentSelectedCityOption, setCurrentSelectedCityOption] = useState<Option | null>(null);

    const handleCityChange = (selectedOption: Option | null) => {
        if (isCustomCityOption(selectedOption)) {
            setShowCustomCityInput(true);
            setFieldValue("city", null);
            setCurrentSelectedCityOption(null);
            return;
        }

        setShowCustomCityInput(false);
        setFieldValue("city", selectedOption ? selectedOption.value : null);
        setFieldValue("customCity", null);
        setCurrentSelectedCityOption(selectedOption);
    };

    const handleCustomCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("customCity", event.target.value);
    };

    return (
        <>
            <Row className="mb-3 mb-sm-4">
                <Col {...labelColumnProps} className="mb-2 mb-sm-0">
                    <label htmlFor={cityFieldId} className="fs-7 fw-bold mt-sm-2 pt-1 required-label">
                        {t("city")}
                    </label>
                </Col>
                <Col {...inputColumnProps}>
                    <ReactAsyncSelect
                        id={cityFieldId}
                        key={`city-${values.country}`}
                        name="city"
                        inputAutoComplete="new-password"
                        options={cityOptions}
                        value={getCitySelectValue({
                            showCustomCityInput,
                            cityOptions,
                            cityValue: values.city,
                            currentSelectedCityOption,
                            selectedCityOption,
                        })}
                        onChange={handleCityChange}
                        placeholder={t("Select City")}
                        isSearchable
                        isLoading={isLoading}
                        onMenuOpen={onMenuOpen}
                        onMenuClose={onMenuClose}
                        onInputChange={onInputChange}
                        onMenuScrollToBottom={onMenuScrollToBottom}
                        disableLocalFilter
                        noOptionsMessage={() => t("No options")}
                        loadingMessage={() => ""}
                    />
                    <ErrorMessage name="city">
                        {(message) => <p className="fs-7 p-1 text-danger m-0">{message}</p>}
                    </ErrorMessage>
                </Col>
            </Row>

            {showCustomCityInput && (
                <Row className="mb-3 mb-sm-4">
                    <Col {...labelColumnProps}>
                        <label htmlFor={customCityFieldId} className="fs-7 fw-bold pt-2 required-label">
                            {t("Add custom city name")}
                        </label>
                    </Col>
                    <Col {...inputColumnProps}>
                        <Field
                            id={customCityFieldId}
                            name="customCity"
                            autoComplete="new-password"
                            placeholder={t("Enter custom city name")}
                            className={`form-control ${errors.customCity && touched.customCity ? "border-danger" : "border-secondary"}`}
                            onChange={handleCustomCityChange}
                            maxLength={50}
                        />
                        <ErrorMessage name="customCity">
                            {(message) => <p className="fs-7 p-1 text-danger m-0">{message}</p>}
                        </ErrorMessage>
                    </Col>
                </Row>
            )}
        </>
    );
};

export default BranchCitySelectFields;
